/**
 * EpsSelect — Selector controlado de EPS con opción "Otra" que permite escribir.
 *
 * Devuelve un string plano (el nombre de la EPS o el texto libre escrito en "Otra").
 * Compatible con react-hook-form mediante <Controller /> o setValue manual.
 */

import { useEffect, useMemo, useState } from "react";
import { Select, Input } from "@/components/ui";
import { EPS_LIST, EPS_OTHER, EPS_NONE } from "@/lib/data/eps";

interface Props {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  helperText?: string;
  /** Si true, agrega "Sisbén / No tengo EPS" como opción adicional. */
  allowNone?: boolean;
  /** Texto del placeholder de la opción vacía. */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function EpsSelect({
  value,
  onChange,
  label,
  helperText,
  allowNone = true,
  placeholder = "Selecciona tu EPS",
  disabled,
  className,
}: Props) {
  // Determinar si el valor actual es una EPS conocida o texto libre ("Otra")
  const isKnown = useMemo(() => {
    if (!value) return true;
    if (allowNone && value === EPS_NONE) return true;
    return EPS_LIST.includes(value);
  }, [value, allowNone]);

  const [otherText, setOtherText] = useState(isKnown ? "" : value);
  const [showOther, setShowOther] = useState(!isKnown && !!value);

  // Sincronizar si el valor externo cambia (ej. al cargar datos del servidor)
  useEffect(() => {
    if (!isKnown && value) {
      setOtherText(value);
      setShowOther(true);
    }
    if (isKnown && showOther && value !== EPS_OTHER) {
      // Si el valor externo se cambia a uno conocido, salir de modo "otra"
      setShowOther(false);
      setOtherText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const selectValue = showOther ? EPS_OTHER : value || "";

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (next === EPS_OTHER) {
      setShowOther(true);
      // Aún no mandamos cambio: esperamos que escriba algo en el input.
      onChange(otherText || "");
    } else {
      setShowOther(false);
      setOtherText("");
      onChange(next);
    }
  };

  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setOtherText(next);
    onChange(next);
  };

  return (
    <div className={className}>
      <Select
        label={label}
        value={selectValue}
        onChange={handleSelectChange}
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {allowNone && <option value={EPS_NONE}>{EPS_NONE}</option>}
        {EPS_LIST.map((eps) => (
          <option key={eps} value={eps}>
            {eps}
          </option>
        ))}
        <option value={EPS_OTHER}>Otra (escribir)</option>
      </Select>

      {showOther && (
        <div className="mt-2">
          <Input
            value={otherText}
            onChange={handleOtherChange}
            placeholder="Escribe el nombre exacto de tu EPS"
            disabled={disabled}
            autoFocus
          />
        </div>
      )}

      {helperText && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
    </div>
  );
}
